import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface StepperStep {
  key: string;
  label: string;
  state: "pending" | "active" | "complete";
}

export function Stepper({ steps }: { steps: StepperStep[] }) {
  return (
    <nav
      aria-label="Form progress"
      className="sticky top-[72px] z-20 mb-4 flex items-start justify-center overflow-x-auto rounded-xl border border-border/60 bg-card/85 px-4 pb-2 pt-2 backdrop-blur-xl"
    >
      {steps.map((step, idx) => (
        <div key={step.key} className="flex flex-shrink-0 items-start">
          <div
            className="flex flex-col items-center gap-1"
            // aria-current="step" tells screen-readers which step is
            // active; without this the only signal was a color swatch.
            aria-current={step.state === "active" ? "step" : undefined}
          >
            <span
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded-full border-2 transition",
                step.state === "pending" && "border-foreground/20 bg-foreground/[0.03]",
                step.state === "active" &&
                  "border-primary bg-primary/20 shadow-[0_0_0_3px_hsl(var(--primary)/0.1)]",
                step.state === "complete" && "border-success bg-success/30",
              )}
            >
              {/* Icon makes "complete" recognizable without relying on
                  the green tint alone — important for color-blind
                  users and for low-contrast environments. */}
              {step.state === "complete" ? (
                <Check
                  className="h-3 w-3 text-success"
                  strokeWidth={3}
                  aria-hidden="true"
                />
              ) : null}
            </span>
            <span
              className={cn(
                "whitespace-nowrap text-[0.65rem] font-medium transition",
                // Pending labels were `text-muted-foreground/60`, which
                // in dark mode reads as a softly-greyed label and in
                // light mode read as nearly invisible. Bump to
                // `foreground/70` so the upcoming-steps strip reads
                // as navigation in both themes, not as disabled text.
                step.state === "pending" && "text-foreground/70 dark:text-muted-foreground/60",
                step.state === "active" && "font-bold text-primary",
                step.state === "complete" && "font-semibold text-success",
              )}
            >
              {step.label}
              <span className="sr-only">
                {step.state === "active"
                  ? " (current step)"
                  : step.state === "complete"
                    ? " (completed)"
                    : " (not started)"}
              </span>
            </span>
          </div>
          {idx < steps.length - 1 ? (
            <span
              aria-hidden="true"
              className="mx-2 mt-2 h-0.5 max-w-[3rem] flex-1 rounded bg-foreground/15 min-w-[12px]"
            />
          ) : null}
        </div>
      ))}
    </nav>
  );
}
